import { action, observable } from 'mobx';
import { autobind } from 'core-decorators';
import BaseStore from '@src/utils/store/base-store';
import useMount from '@src/utils/hooks/use-mount';
import routeConfigs from '@src/pages/app-router/route-configs';
import Toast from '@src/components/toast';
import copy from '@src/components/copy';
import GeneralServices from '@src/services/general';
import tracker from '@src/tracker';
import { isWeiXin } from 'detect-mobile-device';
import {
  ANNIVERSARY_ERROR_CODES,
  ANNIVERSARY_FEEDBACK_MESSAGES,
  ANNIVERSARY_GUEST_INVITE_COPY_SUCCESS_TEXT,
  ANNIVERSARY_INVITE_SHARE_TEXT
} from '@src/constants/anniversary';

const DEFAULT_GUEST_VIEW_MODEL = {
  accountText: '',
  isActive: false,
  isLiked: false,
  mode: 'browser',
  qrVisible: true,
  wechatVisible: false,
  likeButtonVisible: false,
  likedButtonVisible: false,
  joinButtonVisible: true,
  canLike: false,
  isEnded: false,
  errorText: '',
  qrValue: ''
};
const AUTH_REQUIRED_CODE = -10801;
const ALREADY_LIKED_CODE = -20026;
const ALREADY_LIKED_MESSAGE = '您已经为好友点赞共贺过啦~';
const UID_PATTERN = /^[A-Za-z0-9_-]+$/;

function toNumber(value) {
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? 0 : numberValue;
}

function getServiceResponse(error) {
  return (error && error.serviceResponse) || error || {};
}

function getServiceStatus(error) {
  const serviceResponse = getServiceResponse(error);
  return toNumber(serviceResponse.status || serviceResponse.code);
}

function getServiceMessage(error, fallback) {
  const serviceResponse = getServiceResponse(error);
  return serviceResponse.message || serviceResponse.msg || (error && error.message) || fallback;
}

function getServiceData(error) {
  const serviceResponse = getServiceResponse(error);
  return serviceResponse.data || {};
}

function safeDecodeURIComponent(value) {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return '';
  }
}

class GuestPageStore extends BaseStore {
  @observable uid = '';

  @observable loading = false;

  @observable liking = false;

  @observable copyingInvite = false;

  @observable isWechat = false;

  @observable isEnded = false;

  @observable lastErrorMessage = '';

  @observable authRedirectUrl = '';

  @observable guestViewModel = { ...DEFAULT_GUEST_VIEW_MODEL };

  @observable inviteShareSourceLink = '';

  @observable inviteTinyUrl = '';

  @observable inviteCopyText = '';

  @observable inviteTinyUrlLoading = false;

  @observable inviteTinyUrlFailed = false;

  constructor(options = {}) {
    super(options);
    this.toast = options.toast || Toast;
    this.windowRef = options.windowRef || (typeof window !== 'undefined' ? window : null);
    this.detectWeChat = options.detectWeChat || isWeiXin;
    this.createTinyUrl = options.createTinyUrl || GeneralServices.createTinyUrl;
    this.copy = options.copy || copy;
    this.tracker = options.tracker || tracker;
  }

  use(props) {
    super.use(props);
    useMount(() => {
      this.loadPageData();
    });
  }

  /**
   * Guest 页面初始化，只从自身 query 读取 uid 并加载客态信息，生命周期与 Main 页面隔离。
   *
   * @param {string} [search] 测试或路由传入的 query string。
   * @returns {Promise<object>} 客态页 ViewModel。
   */
  @autobind
  @action
  async loadGuest(uid) {
    const nextUid = this.normalizeUid(uid);
    this.uid = nextUid;
    this.isWechat = this.detectWeChatEnvironment();
    this.isEnded = false;
    this.lastErrorMessage = '';
    this.authRedirectUrl = '';

    if (!nextUid) {
      this.resetInviteShareLink();
      this.guestViewModel = this.createGuestViewModel();
      return this.guestViewModel;
    }
    this.prepareInviteShareLink();

    this.loading = true;
    try {
      const data = await this.$ztService.getLikeInfo(nextUid);
      this.isEnded = false;
      this.guestViewModel = this.normalizeGuest(data);
      return this.guestViewModel;
    } catch (error) {
      this.handleLoadFailure(error);
      return this.guestViewModel;
    } finally {
      this.loading = false;
    }
  }

  @autobind
  @action
  async like() {
    if (this.guestViewModel.isLiked) {
      this.showMessage(ALREADY_LIKED_MESSAGE);
      return false;
    }
    if (!this.canSubmitLike()) {
      return false;
    }
    this.trackHelpFriend();
    if (this.authRedirectUrl) {
      this.redirectToAuth(this.authRedirectUrl);
      return false;
    }
    if (this.liking) {
      return this.likePromise || false;
    }

    this.liking = true;
    this.lastErrorMessage = '';
    this.likePromise = this.$ztService
      .doLike(this.uid)
      .then(data => {
        const authUrl = data && data.auth_url;
        if (authUrl) {
          this.authRedirectUrl = authUrl;
          this.redirectToAuth(authUrl);
          return false;
        }
        return this.loadGuest(this.uid).then(() => {
          const isLiked = this.guestViewModel.isLiked === true;
          if (isLiked) {
            this.showMessage('点赞成功！感谢您共贺盛典');
          }
          return isLiked;
        });
      })
      .catch(error => {
        this.handleLikeFailure(error);
        return false;
      })
      .finally(() => {
        this.liking = false;
        this.likePromise = null;
      });
    return this.likePromise;
  }

  @autobind
  @action
  async loadPageData(search) {
    const uid = this.parseUid(search || this.getCurrentSearch());
    return this.loadGuest(uid);
  }

  /**
   * 点击“我也要参加”回到主页面，不携带 Guest 的 uid 状态。
   *
   * @returns {void}
   */
  @autobind
  enterMain() {
    this.trackGuestJoin();
    if (this.$history && this.$history.replace) {
      this.$history.replace(routeConfigs.MAIN.path);
    }
  }

  @autobind
  @action
  copyInviteShare() {
    this.trackGuestInvite();
    if (!this.inviteCopyText) {
      const message = this.inviteTinyUrlLoading ? '链接生成中，请稍后重试' : ANNIVERSARY_FEEDBACK_MESSAGES.loadingFailed;
      this.lastErrorMessage = message;
      this.showMessage(message);
      return false;
    }

    this.copy(this.inviteCopyText, {
      successPreText: ANNIVERSARY_GUEST_INVITE_COPY_SUCCESS_TEXT,
      showText: false
    });
    return true;
  }

  /**
   * 客态页邀请复制预生成短链，避免 iOS 点击后异步请求再复制导致浏览器判定非用户手势。
   *
   * @returns {Promise<string|boolean>|string|boolean} 缓存文案、生成 Promise 或失败状态。
   */
  prepareInviteShareLink() {
    const shareLink = this.getCurrentHref();
    if (!shareLink) {
      this.resetInviteShareLink();
      return false;
    }
    if (this.inviteShareSourceLink === shareLink && (this.inviteCopyText || this.inviteTinyUrlLoading)) {
      return this.inviteCopyText || this.inviteTinyUrlPromise;
    }

    this.inviteShareSourceLink = shareLink;
    this.inviteTinyUrl = '';
    this.inviteCopyText = '';
    this.inviteTinyUrlFailed = false;
    this.inviteTinyUrlLoading = true;
    this.inviteTinyUrlPromise = Promise.resolve(this.createTinyUrl(shareLink))
      .then(data => {
        const tinyUrl = data && data.tiny_url;
        if (!tinyUrl) {
          throw new Error(ANNIVERSARY_FEEDBACK_MESSAGES.loadingFailed);
        }
        this.cacheInviteShareLink(shareLink, tinyUrl);
        return this.inviteCopyText;
      })
      .catch(error => {
        this.handleInviteShareLinkFailure(shareLink, error);
        return false;
      })
      .finally(() => {
        if (this.inviteShareSourceLink === shareLink) {
          this.inviteTinyUrlLoading = false;
          this.inviteTinyUrlPromise = null;
        }
      });
    return this.inviteTinyUrlPromise;
  }

  @action
  cacheInviteShareLink(shareLink, tinyUrl) {
    if (this.inviteShareSourceLink !== shareLink) {
      return '';
    }
    this.inviteTinyUrl = tinyUrl;
    this.inviteCopyText = `${ANNIVERSARY_INVITE_SHARE_TEXT}${tinyUrl}`;
    this.inviteTinyUrlFailed = false;
    return this.inviteCopyText;
  }

  @action
  handleInviteShareLinkFailure(shareLink, error) {
    if (this.inviteShareSourceLink !== shareLink) {
      return false;
    }
    this.lastError = error;
    this.inviteTinyUrl = '';
    this.inviteCopyText = '';
    this.inviteTinyUrlFailed = true;
    this.lastErrorMessage = getServiceMessage(error, ANNIVERSARY_FEEDBACK_MESSAGES.loadingFailed);
    return false;
  }

  @action
  resetInviteShareLink() {
    this.inviteShareSourceLink = '';
    this.inviteTinyUrl = '';
    this.inviteCopyText = '';
    this.inviteTinyUrlLoading = false;
    this.inviteTinyUrlFailed = false;
    this.inviteTinyUrlPromise = null;
  }

  /**
   * 将 getLikeInfo 的原始字段转换成 Guest 静态壳字段。
   * 实调字段为 account/is_active/is_liked，auth_url 仅用于 Store 授权跳转，不暴露给 UI 壳消费。
   *
   * @param {object} data getLikeInfo 返回 data。
   * @returns {object} Guest 页面 ViewModel。
   */
  @action
  normalizeGuest(data = {}) {
    this.authRedirectUrl = data.auth_url || '';
    return this.createGuestViewModel({
      accountText: data.account || '',
      isActive: data.is_active === 1,
      isLiked: data.is_liked === 1
    });
  }

  createGuestViewModel(overrides = {}) {
    const isLiked = overrides.isLiked === true;
    const isActive = overrides.isActive === true;
    const isWechat = this.isWechat === true;
    const canLike = isWechat && !!this.uid && isActive && !isLiked && !this.isEnded;
    return {
      ...DEFAULT_GUEST_VIEW_MODEL,
      mode: isWechat ? 'wechat' : 'browser',
      accountText: overrides.accountText || '',
      isActive,
      isLiked,
      qrVisible: !isWechat,
      wechatVisible: isWechat,
      likeButtonVisible: canLike,
      likedButtonVisible: isWechat && isLiked,
      joinButtonVisible: true,
      canLike,
      isEnded: this.isEnded,
      errorText: this.lastErrorMessage,
      qrValue: this.getCurrentHref()
    };
  }

  canSubmitLike() {
    if (!this.isWechat) {
      return false;
    }
    if (!this.uid || this.isEnded || this.guestViewModel.isLiked) {
      return false;
    }
    if (!this.guestViewModel.isActive && !this.authRedirectUrl) {
      return false;
    }
    return true;
  }

  @action
  handleLoadFailure(error) {
    const status = getServiceStatus(error);
    this.lastErrorMessage = getServiceMessage(error, ANNIVERSARY_FEEDBACK_MESSAGES.loadingFailed);
    if (status === ANNIVERSARY_ERROR_CODES.ACTIVITY_ENDED) {
      this.isEnded = true;
    }
    if (status === AUTH_REQUIRED_CODE) {
      this.authRedirectUrl = getServiceData(error).auth_url || '';
    }
    this.guestViewModel = this.createGuestViewModel({
      isActive: status === AUTH_REQUIRED_CODE,
      isLiked: false
    });
  }

  @action
  handleLikeFailure(error) {
    const status = getServiceStatus(error);
    if (status === AUTH_REQUIRED_CODE) {
      const authUrl = getServiceData(error).auth_url || '';
      this.authRedirectUrl = authUrl;
      this.redirectToAuth(authUrl);
      return;
    }

    this.lastErrorMessage = getServiceMessage(error, ANNIVERSARY_FEEDBACK_MESSAGES.loadingFailed);
    if (status === ANNIVERSARY_ERROR_CODES.ACTIVITY_ENDED) {
      this.isEnded = true;
    }
    this.guestViewModel = this.createGuestViewModel({
      accountText: this.guestViewModel.accountText,
      isActive: this.guestViewModel.isActive,
      isLiked: status === ALREADY_LIKED_CODE
    });
    this.showMessage(this.lastErrorMessage);
  }

  redirectToAuth(authUrl) {
    if (!authUrl || !this.windowRef || !this.windowRef.location) {
      return false;
    }
    if (this.windowRef.location.assign) {
      this.windowRef.location.assign(authUrl);
    } else {
      this.windowRef.location.href = authUrl;
    }
    return true;
  }

  showMessage(message) {
    if (message && this.toast && this.toast.info) {
      this.toast.info(message);
    }
    return message;
  }

  detectWeChatEnvironment() {
    return !!(this.detectWeChat && this.detectWeChat());
  }

  normalizeUid(uid) {
    const nextUid = String(uid || '').trim();
    return UID_PATTERN.test(nextUid) ? nextUid : '';
  }

  parseUid(search = '') {
    const query = String(search).replace(/^\?/, '');
    return query.split('&').reduce((uid, pair) => {
      const [key, value = ''] = pair.split('=');
      return key === 'uid' ? safeDecodeURIComponent(value) : uid;
    }, '');
  }

  getCurrentSearch() {
    if (this.$history && this.$history.location) {
      return this.$history.location.search || '';
    }
    if (typeof window !== 'undefined' && window.location) {
      return window.location.search || '';
    }
    return '';
  }

  getCurrentHref() {
    if (this.windowRef && this.windowRef.location && this.windowRef.location.href) {
      return this.windowRef.location.href;
    }
    if (typeof window !== 'undefined' && window.location) {
      return window.location.href || '';
    }
    return '';
  }

  trackHelpFriend() {
    if (this.tracker && this.tracker.clickHelpFriend) {
      this.tracker.clickHelpFriend();
    }
  }

  trackGuestJoin() {
    if (this.tracker && this.tracker.clickGuestJoin) {
      this.tracker.clickGuestJoin();
    }
  }

  trackGuestInvite() {
    if (this.tracker && this.tracker.clickGuestInvite) {
      this.tracker.clickGuestInvite();
    }
  }
}

export default GuestPageStore;
